import { Copy, RefreshCw } from 'lucide-react'
import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react'
import { useOutletContext } from 'react-router'

import { type EstadoDelPanel, type EstadoDeWebs, type MarcaDeAgencia, marcaDeAgencia, useFichaDeAgencia, useMarcaDeAgencia, useRefrescarAgencia } from '../../api/agencia'
import { ErrorDeLaApi } from '../../api/nucleo'
import { Aviso, Boton, Campo, Casilla, Esqueleto, Pastilla, Selector, Tarjeta, Titulo } from '../../componentes/ui'
import type { Contexto } from '../Modulo'

/**
 * «Mi marca y dominios» (CLAUDE.md §36, R6): cómo ven a la agencia sus clientes. Su logo y su color en el panel,
 * su soporte, el remitente de sus correos y los datos de sus recibos; el dominio de su panel; el dominio bajo el
 * que salen las webs de sus clientes (un registro comodín y cada web sale en «<web>.<su dominio>»); y si quien
 * llega a su panel puede abrirse una cuenta solo. Lo que vale y lo que no lo dice el núcleo.
 */
const mensajeDe = (e: unknown, siNo: string) => (e instanceof ErrorDeLaApi ? e.message : siNo)

const EN_LLANO_PANEL: Record<EstadoDelPanel, [string, 'construyendo' | 'fallida' | 'neutra' | 'publicado']> = {
  'esperando-dns': ['Esperando el registro DNS', 'neutra'],
  conectando: ['Conectando', 'construyendo'],
  certificado: ['Emitiendo el certificado', 'construyendo'],
  conectado: ['Conectado', 'publicado'],
  problema: ['Hay un problema', 'fallida'],
}

const EN_LLANO_WEBS: Record<EstadoDeWebs, [string, 'construyendo' | 'fallida' | 'neutra' | 'publicado']> = {
  apunta: ['Comprobado', 'publicado'],
  'no-apunta': ['El comodín apunta a otro sitio', 'fallida'],
  'sin-registros': ['Esperando el registro comodín', 'neutra'],
  'sin-respuesta': ['No hemos podido comprobarlo ahora', 'construyendo'],
  'sin-comprobar': ['Sin comprobar todavía', 'neutra'],
}

const Registro = ({ tipo, nombre, valor }: { nombre: string; tipo: string; valor: string }) => (
  <div className="mt-3 grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-1 rounded-caja border border-linea bg-papel px-3 py-2 text-[13px]">
    <span className="text-tenue">Tipo</span>
    <code>{tipo}</code>
    <span />
    <span className="text-tenue">Nombre</span>
    <code className="break-all">{nombre}</code>
    <button aria-label="Copiar el nombre" className="text-violeta" onClick={() => void navigator.clipboard?.writeText(nombre)} type="button">
      <Copy aria-hidden className="size-4" />
    </button>
    <span className="text-tenue">Valor</span>
    <code className="break-all">{valor}</code>
    <button aria-label="Copiar el valor" className="text-violeta" onClick={() => void navigator.clipboard?.writeText(valor)} type="button">
      <Copy aria-hidden className="size-4" />
    </button>
  </div>
)

const Imagen = ({ espacio, campo, etiqueta, url, hecho }: { campo: 'favicon' | 'logo' | 'logoOscuro'; espacio: number; etiqueta: string; hecho: (m: MarcaDeAgencia | null, fallo?: string) => void; url?: null | string }) => {
  const [subiendo, setSubiendo] = useState(false)
  const subir = async (fichero: File | null) => {
    setSubiendo(true)
    try {
      hecho(await marcaDeAgencia(espacio).imagen(campo, fichero))
    } catch (e) {
      hecho(null, mensajeDe(e, 'No se ha podido subir la imagen. Vuelve a intentarlo.'))
    } finally {
      setSubiendo(false)
    }
  }
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-caja border border-linea bg-papel">
        {url ? <img alt="" className="max-h-12 max-w-12 object-contain" src={url} /> : <span className="text-xs text-tenue">—</span>}
      </div>
      <div className="min-w-0">
        <p className="text-[13px] font-medium">{etiqueta}</p>
        <div className="mt-1 flex flex-wrap gap-2">
          <label className="cursor-pointer text-[13px] font-medium text-violeta hover:underline">
            {subiendo ? 'Subiendo…' : url ? 'Cambiar' : 'Subir'}
            <input accept="image/png,image/jpeg,image/webp,image/svg+xml" className="sr-only" disabled={subiendo} onChange={(e: ChangeEvent<HTMLInputElement>) => void subir(e.target.files?.[0] ?? null)} type="file" />
          </label>
          {url && (
            <button className="text-[13px] text-tenue hover:underline" disabled={subiendo} onClick={() => void subir(null)} type="button">
              Quitar
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const Formulario = ({ espacio, ficha }: { espacio: number; ficha: MarcaDeAgencia }) => {
  const refrescar = useRefrescarAgencia(espacio)
  const [d, setD] = useState<MarcaDeAgencia>(ficha)
  const [guardando, setGuardando] = useState(false)
  const [mensaje, setMensaje] = useState<null | { tono: 'error' | 'ok'; texto: string }>(null)
  const [dominioPanel, setDominioPanel] = useState(ficha.dominioPanel?.dominio ?? '')
  const [dominioWebs, setDominioWebs] = useState(ficha.dominioWebs?.dominio ?? '')
  useEffect(() => {
    setD(ficha)
    setDominioPanel(ficha.dominioPanel?.dominio ?? '')
    setDominioWebs(ficha.dominioWebs?.dominio ?? '')
  }, [ficha])

  const de = <G extends 'correo' | 'datosFiscales' | 'marca' | 'soporte'>(grupo: G, campo: keyof MarcaDeAgencia[G] & string) => ({
    value: String((d[grupo] as Record<string, unknown>)[campo] ?? ''),
    onChange: (e: { target: { value: string } }) => setD({ ...d, [grupo]: { ...d[grupo], [campo]: e.target.value } }),
  })

  const hacer = async (accion: () => Promise<MarcaDeAgencia>, ok: string) => {
    setGuardando(true)
    setMensaje(null)
    try {
      const nueva = await accion()
      setD(nueva)
      setMensaje({ tono: 'ok', texto: ok })
      void refrescar()
    } catch (e) {
      setMensaje({ tono: 'error', texto: mensajeDe(e, 'No se ha podido guardar. Vuelve a intentarlo.') })
    } finally {
      setGuardando(false)
    }
  }

  const guardar = (e: FormEvent) => {
    e.preventDefault()
    void hacer(
      () =>
        marcaDeAgencia(espacio).guardar({
          marca: { colorPrincipal: d.marca.colorPrincipal ?? null, lema: d.marca.lema ?? null },
          soporte: d.soporte,
          correo: d.correo,
          datosFiscales: d.datosFiscales,
          dominioPanel: dominioPanel.trim() || null,
          dominioWebs: dominioWebs.trim() || null,
          registroPublico: d.registroPublico,
          planDeRegistro: d.planDeRegistro,
        }),
      'Guardado. Los cambios de la marca se ven en tu panel en menos de un minuto.',
    )
  }

  const panel = d.dominioPanel
  const webs = d.dominioWebs
  return (
    <form className="flex flex-col gap-4" onSubmit={guardar}>
      <Tarjeta>
        <h2 className="font-display text-base font-semibold">Tu marca</h2>
        <p className="mt-1 text-[13px] text-tenue">Es lo que ven tus clientes al entrar en tu panel y en los correos que les llegan.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Imagen campo="logo" espacio={espacio} etiqueta="Logo" hecho={(m, f) => (m ? (setD(m), void refrescar()) : setMensaje({ tono: 'error', texto: f ?? '' }))} url={d.marca.logo} />
          <Imagen campo="logoOscuro" espacio={espacio} etiqueta="Logo para el modo oscuro" hecho={(m, f) => (m ? (setD(m), void refrescar()) : setMensaje({ tono: 'error', texto: f ?? '' }))} url={d.marca.logoOscuro} />
          <Imagen campo="favicon" espacio={espacio} etiqueta="Icono de la pestaña" hecho={(m, f) => (m ? (setD(m), void refrescar()) : setMensaje({ tono: 'error', texto: f ?? '' }))} url={d.marca.favicon} />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Campo ayuda="En hexadecimal, por ejemplo #0f766e." etiqueta="Color principal" {...de('marca', 'colorPrincipal')} />
          <Campo ayuda="Una línea en la pantalla de entrar." etiqueta="Lema" maxLength={120} {...de('marca', 'lema')} />
        </div>
      </Tarjeta>

      <Tarjeta>
        <h2 className="font-display text-base font-semibold">Soporte y correos</h2>
        <p className="mt-1 text-[13px] text-tenue">Dónde te escriben tus clientes cuando piden ayuda, y con qué nombre firman los correos que reciben.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Correo de soporte" type="email" {...de('soporte', 'correo')} />
          <Campo ayuda="Con el prefijo del país: +51 987 654 321." etiqueta="WhatsApp" {...de('soporte', 'whatsapp')} />
          <Campo ayuda="Empieza por https://" etiqueta="Página de ayuda" {...de('soporte', 'url')} />
          <Campo ayuda="Sin rellenar, el nombre de tu agencia." etiqueta="Nombre del remitente" {...de('correo', 'nombreRemitente')} />
          <Campo etiqueta="Responder a" type="email" {...de('correo', 'responderA')} />
        </div>
      </Tarjeta>

      <Tarjeta>
        <h2 className="font-display text-base font-semibold">Datos de tus recibos</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Razón social" {...de('datosFiscales', 'razonSocial')} />
          <Campo etiqueta="Identificador fiscal (RUC, NIF…)" {...de('datosFiscales', 'idFiscal')} />
          <Campo etiqueta="Dirección" {...de('datosFiscales', 'direccion')} />
          <Campo ayuda="Dos letras: PE, ES, MX…" etiqueta="País" maxLength={2} {...de('datosFiscales', 'pais')} />
        </div>
      </Tarjeta>

      <Tarjeta>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="font-display text-base font-semibold">El dominio de tu panel</h2>
            <p className="mt-1 text-[13px] text-tenue">Ahora tus clientes entran por {d.direccionDelPanel}. Con un dominio tuyo, entran por él.</p>
          </div>
          {panel && <Pastilla clave={EN_LLANO_PANEL[panel.estado][1]}>{EN_LLANO_PANEL[panel.estado][0]}</Pastilla>}
        </div>
        <div className="mt-3 max-w-md">
          <Campo etiqueta="Dominio del panel" onChange={(e) => setDominioPanel(e.target.value)} placeholder="panel.miagencia.com" value={dominioPanel} />
        </div>
        {panel && panel.estado !== 'conectado' && (
          <>
            <p className="mt-3 text-[13px]">Crea este registro en el proveedor de tu dominio:</p>
            <Registro {...panel.registro} />
            {panel.problema && <p className="mt-2 text-[13px] text-error">{panel.problema}</p>}
          </>
        )}
      </Tarjeta>

      <Tarjeta>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="font-display text-base font-semibold">El dominio de las webs de tus clientes</h2>
            <p className="mt-1 text-[13px] text-tenue">
              Mientras un cliente no conecte su propio dominio, su web sale bajo el tuyo: «mi-cliente.webs.miagencia.com», y su vista previa en «mi-cliente-previa.webs.miagencia.com». Basta un registro comodín.
            </p>
          </div>
          {webs && <Pastilla clave={EN_LLANO_WEBS[webs.estado][1]}>{EN_LLANO_WEBS[webs.estado][0]}</Pastilla>}
        </div>
        <div className="mt-3 max-w-md">
          <Campo etiqueta="Dominio para las webs" onChange={(e) => setDominioWebs(e.target.value)} placeholder="webs.miagencia.com" value={dominioWebs} />
        </div>
        {webs && (
          <>
            {webs.estado !== 'apunta' && <p className="mt-3 text-[13px]">Crea este registro comodín en el proveedor de tu dominio:</p>}
            {webs.estado !== 'apunta' && <Registro {...webs.registro} />}
            {webs.estado === 'apunta' && <p className="mt-3 text-[13px]">Listo: cada web de tus clientes sale en «{webs.ejemplo.replace('mi-cliente', '<web>')}» en cuanto tiene su certificado (unos minutos).</p>}
          </>
        )}
        {(panel || webs) && (
          <div className="mt-3">
            <Boton disabled={guardando} onClick={() => void hacer(() => marcaDeAgencia(espacio).comprobar(), 'Comprobado.')} pequeno>
              <RefreshCw aria-hidden className="size-3.5" /> Comprobar ahora
            </Boton>
          </div>
        )}
      </Tarjeta>

      <Tarjeta>
        <h2 className="font-display text-base font-semibold">Registro de clientes</h2>
        <label className="mt-3 flex items-start gap-2 text-sm">
          <Casilla marcada={d.registroPublico} onChange={(e) => setD({ ...d, registroPublico: e.target.checked })} />
          <span>
            Quien llegue a tu panel puede crearse una cuenta
            <span className="block text-[13px] text-tenue">Sale «Crear una cuenta» en la pantalla de entrar. La cuenta nace como cliente tuyo, con el plan que elijas aquí.</span>
          </span>
        </label>
        <div className="mt-3 max-w-sm">
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Plan de las cuentas nuevas</span>
            <Selector onChange={(e) => setD({ ...d, planDeRegistro: e.target.value ? Number(e.target.value) : null })} value={d.planDeRegistro ?? ''}>
              <option value="">Elige un plan</option>
              {d.planes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </Selector>
          </label>
        </div>
        {d.registroPublico && d.planDeRegistro && <p className="mt-2 text-[13px] text-tenue">Comparte este enlace: {d.direccionDelPanel}/crear-cuenta</p>}
      </Tarjeta>

      {mensaje && <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso>}
      <div className="flex justify-end">
        <Boton cargando={guardando} tipo="primario" type="submit">
          Guardar
        </Boton>
      </div>
    </form>
  )
}

export const Marca = () => {
  const { espacio } = useOutletContext<Contexto>()
  const { data: ficha, isPending: cargandoFicha } = useFichaDeAgencia(espacio)
  const { data, isPending, error } = useMarcaDeAgencia(espacio, Boolean(ficha?.puede.marca))
  if (!cargandoFicha && !ficha?.puede.marca) return <Aviso tono="error">Esta cuenta no es una agencia, o no llevas su marca.</Aviso>
  return (
    <>
      <Titulo descripcion="Cómo te ven tus clientes: tu marca, tus dominios y si pueden crearse una cuenta solos." rotulo={ficha?.agencia.nombre}>
        Mi marca y dominios
      </Titulo>
      {isPending || cargandoFicha ? <Esqueleto /> : error || !data ? <Aviso tono="error">No hemos podido leer tu marca. Vuelve a intentarlo en un momento.</Aviso> : <Formulario espacio={espacio} ficha={data} />}
    </>
  )
}
